import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { Button, Select } from 'antd';

import dayjs from 'dayjs';

import { capitalize } from '../helpers/common';
import { formatDateDifference } from '../helpers/date';
import { sortDates, sortNumbers, sortStrings } from '../helpers/filter';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE, SELECT_OPTIONS, TABLE_SELECT_OUTPUT } from '../constants/common';
import { BLANK_FILTER_TEXT } from '../constants/livestock';
import { TABLE_CONFIG_COLUMNS } from '../constants/table-configurations';

import { COLUMN_SIZE } from '../components/AppTable/constants';
import StatusIndicator from '../components/StatusIndicator';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

export const useLivestockColumns = ({
  columnFilterOptions,
  filteredInfo,
  tableData,
  handleAssignedGeofenceChange,
  handleSelect,
  handleTableValueChange
}) => {
  const history = useHistory();

  const { breedsSelectOptions } = useSelector(state => state.breed);
  // const { list: farms, options: farmOptions } = useSelector(state => state.farm);
  // const { data: geofences, pastures } = useSelector(state => state.geofence);
  const { stockTypes } = useSelector(state => state.animal);

  function handleParentClick({ isMale = true, identifier, record }) {
    identifier === 'Select' ? handleSelect(isMale, record) : history.push(`/animals/${record.id}`);
  }

  return useMemo(
    () => [
      {
        ...commonColumnProperties,
        title: 'Livestock ID',
        dataIndex: 'identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
        filteredValue: filteredInfo.identifier || null,
        fixed: 'left',
        id: 'identifier',
        searchable: true,
        width: COLUMN_SIZE.MD,
        render: (value, record) => <Link to={`/animals/${record.id}`}>{record.identifier}</Link>,
        sorter: (a, b) => sortStrings(a.identifier, b.identifier)
      },
      {
        ...commonColumnProperties,
        title: 'Mgmt Tag ID',
        dataIndex: 'eartag_management_id',
        configColumnKey: TABLE_CONFIG_COLUMNS.MGMT_TAG_ID,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.eartag_management_id || null,
        id: 'eartag_management_id',
        rules: [{ required: true, message: 'Required.' }],
        searchable: true,
        width: COLUMN_SIZE.XL,
        sorter: (a, b) => sortStrings(a.eartag_management_id, b.eartag_management_id)
      },
      {
        ...commonColumnProperties,
        title: 'Sex',
        dataIndex: 'sex',
        configColumnKey: TABLE_CONFIG_COLUMNS.SEX,
        editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: SELECT_OPTIONS.GENDER },
        filteredValue: filteredInfo.sex || null,
        filters: columnFilterOptions.sex,
        id: 'sex',
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.sex : record?.sex === value),
        render: value => capitalize(value),
        sorter: (a, b) => sortStrings(a.sex, b.sex)
      },
      {
        ...commonColumnProperties,
        title: 'Colour',
        dataIndex: 'colour',
        configColumnKey: TABLE_CONFIG_COLUMNS.COLOUR,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.colour || null,
        id: 'colour',
        searchable: true,
        width: COLUMN_SIZE.SM,
        sorter: (a, b) => sortStrings(a.colour, b.colour)
      },
      {
        ...commonColumnProperties,
        title: 'Breed',
        dataIndex: 'breedName',
        configColumnKey: TABLE_CONFIG_COLUMNS.BREED,
        editConfig: {
          type: EDIT_COMPONENT_TYPE.SELECT,
          options: breedsSelectOptions,
          output: TABLE_SELECT_OUTPUT.OPTION
        },
        filterSearch: true,
        filteredValue: filteredInfo.breedName || null,
        filters: columnFilterOptions.breeds,
        id: 'breedName',
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.breedName : record?.breedName === value),
        sorter: (a, b) => sortStrings(a.breedName, b.breedName)
      },
      {
        ...commonColumnProperties,
        title: 'Kraal Tag ID',
        dataIndex: 'tagIdentifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.KRAAL_TAG_ID,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.tagIdentifier || null,
        id: 'tagIdentifier',
        searchable: true,
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => {
          const searchField = record.tagIdentifier || 'Unlinked';

          return searchField.toString().toLowerCase().includes(value.toLowerCase());
        },
        render: (value, record) => (record?.tag?.id ? <Link to={`/tag/${record?.tag?.id}`}>{value}</Link> : 'Unlinked'),
        sorter: (a, b) => sortStrings(a.tagIdentifier, b.tagIdentifier)
      },
      {
        ...commonColumnProperties,
        title: 'Brand',
        dataIndex: 'brand',
        configColumnKey: TABLE_CONFIG_COLUMNS.BRAND,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        editable: true,
        filteredValue: filteredInfo.brand || null,
        id: 'brand',
        searchable: true,
        width: COLUMN_SIZE.MD,
        sorter: (a, b) => sortStrings(a.brand, b.brand)
      },
      {
        ...commonColumnProperties,
        title: 'Farm',
        dataIndex: 'farm',
        configColumnKey: TABLE_CONFIG_COLUMNS.FARM,
        // editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: farmOptions || [], valueKey: 'id' },
        filterSearch: true,
        filteredValue: filteredInfo.farm || null,
        filters: columnFilterOptions.farms,
        id: 'farm',
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.farm?.name : record?.farm?.name === value),
        render: (value, record) => record.farmName || record?.farm?.name,
        sorter: (a, b) => sortStrings(a.farmName, b.farmName)
      },
      {
        ...commonColumnProperties,
        title: 'Assigned Pasture',
        dataIndex: 'assignedGeofence',
        configColumnKey: TABLE_CONFIG_COLUMNS.ASSIGNED_GEOFENCE,
        // editable: true,
        width: COLUMN_SIZE.XL,
        filterSearch: true,
        filters: columnFilterOptions.locations,
        filteredValue: filteredInfo.assignedGeofence || null,
        id: 'assignedGeofence',
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT
            ? !record?.assigned_geofences.length
            : record?.assigned_geofences?.includes(value),
        render: (value, record) => {
          return record.assigned_geofences.at(0);
          // const farm = farms?.find(i => i.id === record.farm_id);
          // const currentGeofence = record.geofences?.find(g => !g?.is_master);
          // const farmGeofences = farm?.geofences.filter(g => !g?.is_master);
          // let geofenceValue;
          //
          // if (farmGeofences?.find(g => g?.id === currentGeofence?.id)) {
          //   geofenceValue = {
          //     ...currentGeofence,
          //     value: currentGeofence.value,
          //     label: currentGeofence.name
          //   };
          // }
          //
          // const handleOnChange = (value, option) => {
          //   const tmp = geofences?.find(g => g?.id === value);
          //   const updatedRecord = { ...record, geofences: [{ ...tmp }], assigned_geofences: [{ ...tmp }] };
          //   const updatedTableData = tableData.map(item =>
          //     item.id === updatedRecord.id ? { ...item, ...updatedRecord } : item
          //   );
          //   handleAssignedGeofenceChange({
          //     recordId: record.id,
          //     tableData: updatedTableData,
          //     isGeofenceChanged: true
          //   });
          // };

          // return (
          //   <Select
          //     size="small"
          //     variant="borderless"
          //     options={pastures?.filter(p => p.farm_id === record.farm_id).map(p => ({ label: p.name, value: p.id }))}
          //     style={{ height: '21px', width: '100%' }}
          //     value={geofenceValue ?? record.assigned_geofences?.at(0)}
          //     onChange={handleOnChange}
          //   />
          // );
        },
        sorter: (a, b) => sortStrings(a.assigned_geofences.at(0), b.assigned_geofences.at(0))
      },
      {
        ...commonColumnProperties,
        title: 'Livestock Type',
        dataIndex: 'stock_type',
        configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_TYPE,
        editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: stockTypes || [] },
        filterSearch: true,
        filteredValue: filteredInfo.stock_type || null,
        filters: columnFilterOptions.types,
        id: 'livestockType',
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.livestockType : record?.livestockType === value,
        onCell: d => (d.livestockType?.label ? d.livestockType.label : d.livestockType) ?? '',
        render: value => capitalize(value),
        sorter: (a, b) => sortStrings(a.livestockType, b.livestockType)
      },
      {
        ...commonColumnProperties,
        title: 'Age',
        dataIndex: 'dob_at',
        configColumnKey: TABLE_CONFIG_COLUMNS.AGE,
        filteredValue: filteredInfo.dob_at || null,
        id: 'age',
        searchable: true,
        width: COLUMN_SIZE.MD,
        onFilter: (value, record) => {
          const age = formatDateDifference(record.dob_at);

          return age.includes(value.toLowerCase());
        },
        render: (value, record) => formatDateDifference(record.dob_at),
        sorter: (a, b) => sortDates(a.dob_at, b.dob_at)
      },
      {
        ...commonColumnProperties,
        title: 'Weight',
        dataIndex: 'weight',
        configColumnKey: TABLE_CONFIG_COLUMNS.WEIGHT,
        filteredValue: filteredInfo.weight || null,
        id: 'weight',
        searchable: true,
        width: COLUMN_SIZE.SM,
        sorter: (a, b) => sortStrings(a.weight, b.weight)
      },
      {
        ...commonColumnProperties,
        title: 'Breeding Status',
        dataIndex: 'breeding_status',
        configColumnKey: TABLE_CONFIG_COLUMNS.BREEDING_STATUS,
        filterSearch: true,
        filteredValue: filteredInfo.breeding_status || null,
        filters: columnFilterOptions.breedingStatuses,
        id: 'breedingStatus',
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.breeding_status : record?.breeding_status === value,
        sorter: (a, b) => sortStrings(a.breeding_status, b.breeding_status)
      },
      {
        ...commonColumnProperties,
        title: 'Calf ID',
        dataIndex: 'calf_livestock_identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.CALF_ID,
        filteredValue: filteredInfo.calf_livestock_identifier || null,
        id: 'calfLivestockId',
        searchable: true,
        width: COLUMN_SIZE.MD,
        render: (value, record) =>
          record ? <Link to={`/animals/${record.calf_livestock_id}`}>{record.calf_livestock_identifier}</Link> : null,
        sorter: (a, b) => sortStrings(a.calf_livestock_identifier, b.calf_livestock_identifier)
      },
      {
        ...commonColumnProperties,
        title: 'Calf Mgmt Tag ID',
        dataIndex: 'calf_mgmt_tag_id',
        configColumnKey: TABLE_CONFIG_COLUMNS.CALF_MGMT_TAG_ID,
        filteredValue: filteredInfo.calf_mgmt_tag_id || null,
        id: 'calfMgmtId',
        searchable: true,
        width: COLUMN_SIZE.MD,
        render: (value, record) => record.calf_mgmt_tag_id,
        sorter: (a, b) => sortStrings(a.calf_livestock_identifier, b.calf_livestock_identifier)
      },
      {
        ...commonColumnProperties,
        title: 'Calf Age',
        dataIndex: 'calf_age',
        configColumnKey: TABLE_CONFIG_COLUMNS.CALF_AGE,
        filteredValue: filteredInfo.calf_age || null,
        id: 'calfAge',
        searchable: true,
        width: COLUMN_SIZE.SM,
        onFilter: (value, record) => {
          const age = formatDateDifference(record.calf_age);

          return age.includes(value.toLowerCase());
        },
        render: (value, record) => formatDateDifference(record.calf_age),
        sorter: (a, b) => sortDates(a.calf_age, b.calf_age)
      },
      {
        ...commonColumnProperties,
        title: 'Connection Status',
        dataIndex: 'connectionStatus',
        configColumnKey: TABLE_CONFIG_COLUMNS.CONNECTION_STATUS,
        filteredValue: filteredInfo.connectionStatus || null,
        filters: columnFilterOptions.connectionStatuses,
        id: 'connectionStatus',
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.tag?.status?.length : record?.tag?.status?.includes(value),
        render: (value, record) => {
          const status = record?.tag?.status;
          const id = record?.id;

          return (
            record && (
              <div className="d-flex align-items-center">
                <StatusIndicator key={id} status={status} />
                {capitalize(status)}
              </div>
            )
          );
        },
        sorter: (a, b) => sortStrings(a?.tag?.status, b?.tag?.status)
      },
      {
        ...commonColumnProperties,
        title: 'Last Reading',
        dataIndex: 'lastReading',
        configColumnKey: TABLE_CONFIG_COLUMNS.LAST_READING,
        filteredValue: filteredInfo.lastReading || null,
        id: 'lastReading',
        searchable: true,
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => {
          const date = record?.tag?.timestamp_at ? dayjs(record?.tag?.timestamp_at).format(DATE_FORMAT.DATETIME) : '';

          return date.includes(value);
        },
        render: (value, record) => {
          const lastReadingTime = record?.tag?.timestamp_at;

          return lastReadingTime ? dayjs(lastReadingTime).format(DATE_FORMAT.DATETIME) : null;
        },
        sorter: (a, b) => sortDates(a.tag?.timestamp_at, b.tag?.timestamp_at)
      },
      {
        ...commonColumnProperties,
        title: 'Official Tag ID',
        dataIndex: 'eartag_official_id',
        configColumnKey: TABLE_CONFIG_COLUMNS.OFFICIAL_TAG_ID,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.eartag_official_id || null,
        id: 'officialTagId',
        searchable: true,
        width: COLUMN_SIZE.XL,
        sorter: (a, b) => sortStrings(a.officialTagId, b.officialTagId)
      },
      {
        ...commonColumnProperties,
        title: 'Ear Mark',
        dataIndex: 'ear_mark',
        configColumnKey: TABLE_CONFIG_COLUMNS.EAR_MARK,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.ear_mark || null,
        id: 'earMark',
        searchable: true,
        width: COLUMN_SIZE.XL,
        sorter: (a, b) => sortStrings(a.earMark, b.earMark)
      },
      {
        ...commonColumnProperties,
        title: 'Specie',
        dataIndex: 'specie',
        configColumnKey: TABLE_CONFIG_COLUMNS.SPECIE,
        editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: SELECT_OPTIONS.SPECIE },
        filteredValue: filteredInfo.specie || null,
        id: 'specie',
        searchable: true,
        width: COLUMN_SIZE.LG,
        render: (value, record) => (record.specieName?.label ? record.specieName.label : record.specieName) ?? '',
        sorter: (a, b) => sortStrings(a.specieName, b.specieName)
      },
      {
        ...commonColumnProperties,
        title: 'Date of Birth',
        dataIndex: 'dob',
        configColumnKey: TABLE_CONFIG_COLUMNS.DOB,
        editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
        filteredValue: filteredInfo.dob || null,
        id: 'dob',
        searchable: true,
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => {
          const date = record.dob ? dayjs(record.dob).format(DATE_FORMAT.DATE) : '';

          return date.includes(value);
        },
        render: (value, record) => (record.dob ? dayjs(record.dob).format(DATE_FORMAT.DATE) : null),
        sorter: (a, b) => sortDates(a.dob, b.dob)
      },
      {
        ...commonColumnProperties,
        title: 'Polled/Horned',
        dataIndex: 'polled_horned',
        configColumnKey: TABLE_CONFIG_COLUMNS.POLLED,
        editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: SELECT_OPTIONS.POLLED_HORNED },
        filteredValue: filteredInfo.polledHorned || null,
        filters: columnFilterOptions.polledHorned,
        id: 'polledHorned',
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.polledHorned : record?.polledHorned === value,
        // render: (value, record) => (record.polledHorned?.label ? record.polledHorned.label : record.polledHorned) ?? '',
        sorter: (a, b) => sortStrings(a.polledHorned, b.polledHorned)
      },
      {
        ...commonColumnProperties,
        title: 'Description',
        dataIndex: 'description',
        configColumnKey: TABLE_CONFIG_COLUMNS.DESCRIPTION,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.name || null,
        id: 'description',
        searchable: true,
        width: COLUMN_SIZE.XL,
        sorter: (a, b) => sortStrings(a.name, b.name)
      },
      {
        ...commonColumnProperties,
        title: 'Sire ID',
        dataIndex: 'sire_identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.SIRE_ID,
        filteredValue: filteredInfo.sire_identifier || null,
        id: 'sireId',
        noPadding: true,
        searchable: true,
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => record.sire_identifier?.toString().toLowerCase().includes(value.toLowerCase()),
        render: (value, record) => {
          const identifier = record.sireId ? record.sire_identifier : 'Select';

          return (
            <>
              <Button size="small" type="link" onClick={() => handleParentClick({ identifier, record })}>
                {identifier}
              </Button>
              {identifier !== 'Select' && (
                <Button
                  size="small"
                  type="text"
                  className="float-right"
                  onClick={() => handleTableValueChange('sireId', '', record)}>
                  clear
                </Button>
              )}
            </>
          );
        },
        sorter: (a, b) => {
          const strA = a.sire_identifier ? a.sire_identifier : 'Select';
          const strB = b.sire_identifier ? b.sire_identifier : 'Select';

          return sortStrings(strA, strB);
        }
      },
      {
        ...commonColumnProperties,
        title: 'Dam ID',
        dataIndex: 'dam_identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.DAM_ID,
        filteredValue: filteredInfo.dam_identifier || null,
        id: 'damId',
        noPadding: true,
        searchable: true,
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => record.dam_identifier?.toString().toLowerCase().includes(value.toLowerCase()),
        render: (value, record) => {
          const identifier = record.damId ? record.dam_identifier : 'Select';

          return (
            <>
              <Button size="small" type="link" onClick={() => handleParentClick({ isMale: false, identifier, record })}>
                {identifier}
              </Button>
              {identifier !== 'Select' && (
                <Button
                  size="small"
                  type="text"
                  className="float-right"
                  onClick={() => handleTableValueChange('damId', '', record)}>
                  clear
                </Button>
              )}
            </>
          );
        },
        sorter: (a, b) => {
          const strA = a.dam_identifier ? a.dam_identifier : 'Select';
          const strB = b.dam_identifier ? b.dam_identifier : 'Select';

          return sortStrings(strA, strB);
        }
      },
      {
        ...commonColumnProperties,
        title: 'Number of calves',
        dataIndex: 'numbers_calves',
        configColumnKey: TABLE_CONFIG_COLUMNS.NUM_OF_CALVES,
        filteredValue: filteredInfo.numbers_calves || null,
        id: 'numbersCalves',
        searchable: true,
        width: COLUMN_SIZE.XS,
        sorter: (a, b) => sortNumbers(a.numbers_calves, b.numbers_calves)
      },
      {
        ...commonColumnProperties,
        title: 'Purchase Date',
        dataIndex: 'purchase_date',
        configColumnKey: TABLE_CONFIG_COLUMNS.PURCHASE_DATE,
        editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
        filteredValue: filteredInfo.purchase_date || null,
        id: 'purchaseDate',
        searchable: true,
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => {
          const date = record.purchaseDate ? dayjs(record.purchaseDate).format(DATE_FORMAT.DATE) : '';

          return date.includes(value);
        },
        render: value => (value ? dayjs(value).format(DATE_FORMAT.DATE) : null),
        sorter: (a, b) => sortDates(a.purchaseDate, b.purchaseDate)
      },
      {
        ...commonColumnProperties,
        title: 'Purchase From',
        dataIndex: 'purchased_from',
        configColumnKey: TABLE_CONFIG_COLUMNS.PURCHASE_FROM,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.purchased_from || null,
        id: 'purchasedFrom',
        searchable: true,
        width: COLUMN_SIZE.MD,
        sorter: (a, b) => sortStrings(a.purchasedFrom, b.purchasedFrom)
      },
      {
        ...commonColumnProperties,
        title: 'Purchase Where',
        dataIndex: 'purchased_where',
        configColumnKey: TABLE_CONFIG_COLUMNS.PURCHASE_WHERE,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.purchased_where || null,
        id: 'purchasedWhere',
        searchable: true,
        width: COLUMN_SIZE.MD,
        sorter: (a, b) => sortStrings(a.purchasedWhere, b.purchasedWhere)
      },
      {
        ...commonColumnProperties,
        title: 'Purchase Notes',
        dataIndex: 'purchase_notes',
        configColumnKey: TABLE_CONFIG_COLUMNS.PURCHASE_NOTES,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.purchase_notes || null,
        id: 'purchaseNotes',
        searchable: true,
        width: COLUMN_SIZE.XL,
        sorter: (a, b) => sortStrings(a.purchaseNotes, b.purchaseNotes)
      },
      {
        ...commonColumnProperties,
        title: 'Date Sold',
        dataIndex: 'date_sold',
        configColumnKey: TABLE_CONFIG_COLUMNS.DATE_SOLD,
        editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
        filteredValue: filteredInfo.date_sold || null,
        id: 'dateSold',
        searchable: true,
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => {
          const date = record.date_sold ? dayjs(record.date_sold).format(DATE_FORMAT.DATE) : '';

          return date.includes(value);
        },
        render: value => (value ? dayjs(value).format(DATE_FORMAT.DATE) : null),
        sorter: (a, b) => sortDates(a.date_sold, b.date_sold)
      },
      {
        ...commonColumnProperties,
        title: 'Sold To',
        dataIndex: 'sold_to',
        configColumnKey: TABLE_CONFIG_COLUMNS.SOLD_TO,
        filteredValue: filteredInfo.sold_to || null,
        id: 'soldTo',
        searchable: true,
        width: COLUMN_SIZE.MD,
        sorter: (a, b) => sortStrings(a.soldTo, b.soldTo)
      },
      {
        ...commonColumnProperties,
        title: 'Sold Location',
        dataIndex: 'sold_when',
        configColumnKey: TABLE_CONFIG_COLUMNS.SOLD_WHEN,
        filteredValue: filteredInfo.sold_when || null,
        id: 'soldWhen',
        searchable: true,
        width: COLUMN_SIZE.MD,
        sorter: (a, b) => sortStrings(a.soldWhen, b.soldWhen)
      },
      {
        ...commonColumnProperties,
        title: 'Loss Date',
        dataIndex: 'loss_date',
        configColumnKey: TABLE_CONFIG_COLUMNS.LOSS_DATE,
        editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
        filteredValue: filteredInfo.lossDate || null,
        id: 'lossDate',
        searchable: true,
        width: COLUMN_SIZE.XL,
        onFilter: (value, record) => {
          const date = record.lossDate ? dayjs(record.lossDate).format(DATE_FORMAT.DATE) : '';

          return date.includes(value);
        },
        render: value => (value ? dayjs(value).format(DATE_FORMAT.DATE) : null),
        sorter: (a, b) => sortDates(a.loss_date, b.loss_date)
      },
      {
        ...commonColumnProperties,
        title: 'Loss Reason',
        dataIndex: 'loss_reason',
        configColumnKey: TABLE_CONFIG_COLUMNS.LOSS_REASON,
        filteredValue: filteredInfo.lossReason || null,
        filters: columnFilterOptions.lossReason,
        id: 'lossReason',
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.lossReason : record?.lossReason === value),
        sorter: (a, b) => sortStrings(a.lossReason, b.lossReason)
      },
      {
        title: 'Comments',
        dataIndex: 'comments',
        configColumnKey: TABLE_CONFIG_COLUMNS.COMMENTS,
        editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
        filteredValue: filteredInfo.comments || null,
        id: 'comments',
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        width: COLUMN_SIZE.LG,
        sorter: (a, b) => sortStrings(a.comments, b.comments)
      }
    ],
    [columnFilterOptions, filteredInfo, tableData, handleAssignedGeofenceChange, handleSelect, handleTableValueChange]
  );
};
